<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useAlert } from 'dashboard/composables';
import uazapiChannelAPI from 'dashboard/api/channel/uazapiChannel';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

// Estados
const status = ref('connecting');
const qrcode = ref('');
const isConnected = computed(() => status.value === 'open' || status.value === 'connected');
const isLoading = ref(false);
const isSaving = ref(false);
const showQRModal = ref(false);
const showToken = ref(false);
const pollingInterval = ref(null);

// Formulário de Edição
const inboxName = ref(props.inbox.name || '');
const apiHost = ref(props.inbox.provider_config?.api_host || 'https://free.uazapi.com');
const apiKey = ref(props.inbox.provider_config?.api_key || '');
const instanceName = ref(props.inbox.provider_config?.instance_name || '');

// Buscar Status da Conexão
const fetchStatus = async () => {
  if (!props.inbox?.id) return;
  try {
    const res = await uazapiChannelAPI.getStatus(props.inbox.id);
    status.value = res.data?.state || res.data?.status || 'close';
  } catch (err) {
    status.value = 'close';
  }
};

// Salvar Credenciais & Testar
const saveConfiguration = async () => {
  if (!apiKey.value.trim()) {
    useAlert('Por favor, informe o Token da UazAPI.');
    return;
  }

  isSaving.value = true;
  try {
    const res = await uazapiChannelAPI.updateConfig(props.inbox.id, {
      name: inboxName.value.trim(),
      api_host: apiHost.value.trim(),
      api_key: apiKey.value.trim(),
      instance_name: instanceName.value.trim(),
    });

    status.value = res.data?.status?.state || res.data?.status?.status || 'close';
    useAlert('Configurações atualizadas com sucesso! ✅');

    if (!isConnected.value) {
      handleReconnect();
    }
  } catch (err) {
    console.error('Erro ao atualizar configurações:', err);
    useAlert(err.response?.data?.error || 'Erro ao atualizar configurações da UazAPI.');
  } finally {
    isSaving.value = false;
  }
};

// Abrir Modal de QR Code para Reconectar
const handleReconnect = async () => {
  isLoading.value = true;
  showQRModal.value = true;
  qrcode.value = '';

  try {
    const res = await uazapiChannelAPI.reconnect(props.inbox.id);
    qrcode.value = res.data?.qrcode || '';
    startPolling();
  } catch (err) {
    console.error('Erro ao reconectar:', err);
    useAlert('Erro ao gerar novo QR Code na UazAPI.');
  } finally {
    isLoading.value = false;
  }
};

// Desconectar / Fazer Logout
const handleDisconnect = async () => {
  if (!confirm('Tem certeza que deseja desconectar este WhatsApp da UazAPI?')) return;

  isLoading.value = true;
  try {
    await uazapiChannelAPI.disconnect(props.inbox.id);
    status.value = 'close';
    useAlert('WhatsApp desconectado com sucesso.');
  } catch (err) {
    console.error('Erro ao desconectar:', err);
    useAlert('Erro ao desconectar instância.');
  } finally {
    isLoading.value = false;
  }
};

// Polling de detecção do QR Code
const startPolling = () => {
  if (pollingInterval.value) clearInterval(pollingInterval.value);
  pollingInterval.value = setInterval(async () => {
    try {
      const res = await uazapiChannelAPI.getStatus(props.inbox.id);
      const state = res.data?.state || res.data?.status;
      if (state === 'open' || state === 'connected') {
        status.value = 'open';
        showQRModal.value = false;
        clearInterval(pollingInterval.value);
        useAlert('WhatsApp reconectado com sucesso! 🎉');
      }
    } catch (err) {
      console.warn('Polling status...', err);
    }
  }, 3500);
};

onMounted(() => {
  fetchStatus();
});

onUnmounted(() => {
  if (pollingInterval.value) clearInterval(pollingInterval.value);
});
</script>

<template>
  <div class="space-y-6 text-slate-100 max-w-4xl">
    <!-- 🟢 1. CARD DE STATUS DA CONEXÃO -->
    <div class="border border-slate-750/40 rounded-2xl p-6 bg-slate-900 shadow-xl space-y-5">
      <div class="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-5 border-b border-slate-800">
        <div class="flex items-center gap-3.5">
          <div
            class="w-12 h-12 rounded-2xl flex items-center justify-center text-2xl shadow-sm border transition-all"
            :class="isConnected ? 'bg-emerald-500/15 border-emerald-500/30 text-emerald-400' : 'bg-rose-500/15 border-rose-500/30 text-rose-400'"
          >
            {{ isConnected ? '🟢' : '🔴' }}
          </div>
          <div>
            <div class="flex items-center gap-2.5">
              <h3 class="text-base font-bold text-white">Status da Conexão WhatsApp</h3>
              <span
                class="px-2.5 py-0.5 text-xs font-bold rounded-full border transition-all"
                :class="isConnected ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' : 'bg-rose-500/20 text-rose-300 border-rose-500/30'"
              >
                {{ isConnected ? 'Conectado / Online' : 'Desconectado / Offline' }}
              </span>
            </div>
            <p class="text-xs text-slate-400 mt-0.5">
              Instância UazAPI: <strong class="font-mono text-slate-200">{{ instanceName || inbox.id }}</strong>
            </p>
          </div>
        </div>

        <!-- Ações de Conexão -->
        <div class="flex items-center gap-2.5">
          <button
            type="button"
            :disabled="isLoading"
            class="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold rounded-xl transition-all shadow-md flex items-center gap-2 cursor-pointer disabled:opacity-50"
            @click="handleReconnect"
          >
            <span>🔄</span>
            <span>Reconectar com QR Code</span>
          </button>

          <button
            v-if="isConnected"
            type="button"
            :disabled="isLoading"
            class="px-3.5 py-2 bg-rose-600/20 hover:bg-rose-600/30 text-rose-300 border border-rose-500/30 text-xs font-semibold rounded-xl transition-all flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
            @click="handleDisconnect"
          >
            <span>Desconectar</span>
          </button>
        </div>
      </div>

      <p class="text-xs text-slate-400 leading-relaxed">
        Se o seu token na UazAPI mudou ou você deseja trocar de número de WhatsApp, você pode atualizar as credenciais abaixo e gerar um novo QR Code imediatamente.
      </p>
    </div>

    <!-- ⚙️ 2. FORMULÁRIO DE EDIÇÃO DAS CREDENCIAIS -->
    <div class="border border-slate-750/40 rounded-2xl p-6 bg-slate-900 shadow-xl space-y-5">
      <div class="flex items-center gap-3 pb-4 border-b border-slate-800">
        <div class="w-8 h-8 rounded-xl bg-slate-800 border border-slate-700 flex items-center justify-center text-sm">
          ⚙️
        </div>
        <div>
          <h4 class="text-sm font-bold text-white">Editar Credenciais da UazAPI</h4>
          <p class="text-xs text-slate-400">Atualize a URL, Token de Acesso ou Nome da Instância.</p>
        </div>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block text-xs font-semibold text-slate-300 mb-1.5">
            Nome da Caixa de Entrada <span class="text-rose-400">*</span>
          </label>
          <input
            v-model="inboxName"
            type="text"
            placeholder="Nome da Caixa de Entrada"
            class="w-full px-3.5 py-2.5 text-xs bg-slate-950 border border-slate-750 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 transition-all"
          />
        </div>

        <div>
          <label class="block text-xs font-semibold text-slate-300 mb-1.5">
            Nome da Instância na UazAPI <span class="text-slate-500 font-normal">(Ex: IIK4ZN)</span>
          </label>
          <input
            v-model="instanceName"
            type="text"
            placeholder="Ex: IIK4ZN"
            class="w-full px-3.5 py-2.5 text-xs bg-slate-950 border border-slate-750 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 font-mono transition-all"
          />
        </div>

        <div>
          <label class="block text-xs font-semibold text-slate-300 mb-1.5">
            URL da UazAPI <span class="text-rose-400">*</span>
          </label>
          <input
            v-model="apiHost"
            type="text"
            placeholder="https://free.uazapi.com ou servidor próprio"
            class="w-full px-3.5 py-2.5 text-xs bg-slate-950 border border-slate-750 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 font-mono transition-all"
          />
        </div>

        <div>
          <div class="flex items-center justify-between mb-1.5">
            <label class="block text-xs font-semibold text-slate-300">
              Token / API Key da UazAPI <span class="text-rose-400">*</span>
            </label>
            <button
              type="button"
              class="text-[10px] text-emerald-400 hover:underline cursor-pointer"
              @click="showToken = !showToken"
            >
              {{ showToken ? 'Ocultar' : 'Exibir Token' }}
            </button>
          </div>
          <input
            v-model="apiKey"
            :type="showToken ? 'text' : 'password'"
            placeholder="Cole aqui seu novo token da UazAPI"
            class="w-full px-3.5 py-2.5 text-xs bg-slate-950 border border-slate-750 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 font-mono transition-all"
          />
        </div>
      </div>

      <div class="pt-4 flex justify-end">
        <button
          type="button"
          :disabled="isSaving"
          class="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-xs font-bold rounded-xl transition-all shadow-md flex items-center gap-2 cursor-pointer"
          @click="saveConfiguration"
        >
          <span v-if="isSaving" class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          <span>{{ isSaving ? 'Salvando e Testando...' : '💾 Salvar e Atualizar Conexão' }}</span>
        </button>
      </div>
    </div>

    <!-- 📱 3. MODAL DE QR CODE PARA RECONEXÃO -->
    <div
      v-if="showQRModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
    >
      <div class="bg-slate-900 border border-slate-750 rounded-3xl p-6 max-w-md w-full shadow-2xl space-y-5 text-center relative">
        <button
          type="button"
          class="absolute top-4 right-4 text-slate-400 hover:text-white text-lg w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center cursor-pointer"
          @click="showQRModal = false"
        >
          ✕
        </button>

        <div class="space-y-1">
          <h3 class="text-base font-bold text-white">Reconectar WhatsApp</h3>
          <p class="text-xs text-slate-400">
            Abra o WhatsApp no celular ➔ <strong>Aparelhos Conectados</strong> ➔ <strong>Conectar um aparelho</strong> e aponte para a imagem abaixo.
          </p>
        </div>

        <div class="p-4 bg-white rounded-2xl shadow-xl border-4 border-emerald-500/40 flex items-center justify-center mx-auto w-64 h-64">
          <img
            v-if="qrcode"
            :src="qrcode.startsWith('data:') ? qrcode : `data:image/png;base64,${qrcode}`"
            alt="QR Code WhatsApp"
            class="w-full h-full object-contain"
          />
          <div v-else class="flex flex-col items-center justify-center text-slate-600 gap-2">
            <div class="w-8 h-8 border-3 border-emerald-600 border-t-transparent rounded-full animate-spin" />
            <span class="text-xs font-semibold">Gerando QR Code...</span>
          </div>
        </div>

        <div class="flex items-center justify-center gap-2 text-xs text-amber-400 bg-amber-500/10 px-3.5 py-2 rounded-xl border border-amber-500/20">
          <span class="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
          <span>Aguardando leitura do aparelho no WhatsApp...</span>
        </div>

        <div class="pt-2">
          <button
            type="button"
            class="w-full py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-semibold rounded-xl transition-all cursor-pointer"
            @click="handleReconnect"
          >
            🔄 Atualizar QR Code
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
